import { useState, type FormEvent } from 'react';
import { SITE } from '@/config/site';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const fd = new FormData(formEl);

    const honeypot = String(fd.get('company_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('已收到你的留言，我们会在两个工作日内回复。');
      formEl.reset();
      setCount(0);
      return;
    }

    fd.delete('company_alt');
    const body = new URLSearchParams(fd as unknown as Record<string, string>).toString();

    try {
      setStatus('loading');
      setMessage('');
      const res = await fetch(SITE.contactFormAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试。';

      if (res.ok && code === 'OK') {
        setStatus('success');
        setMessage('已收到你的留言，我们会在两个工作日内回复。');
        formEl.reset();
        setCount(0);
      } else {
        setStatus('error');
        setMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，留言没有发送成功，请稍后重试。');
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100';

  return (
    <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
      <div className="flex flex-col gap-2">
        <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">称呼</label>
        <input id="contact-name" name="name" type="text" required placeholder="怎么称呼你" className={inputClass} />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">邮箱</label>
        <input id="contact-email" name="email" type="email" required placeholder="you@example.com" className={inputClass} />
      </div>
      <div className="flex flex-col gap-2 sm:col-span-2">
        <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">留言类型</label>
        <select id="contact-topic" name="topic" className={`${inputClass} cursor-pointer`}>
          <option value="内容更正">内容更正</option>
          <option value="话题推荐">话题推荐</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他反馈">其他反馈</option>
        </select>
      </div>
      <div className="flex flex-col gap-2 sm:col-span-2">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          onChange={(e) => setCount(e.target.value.length)}
          placeholder="请尽量描述清楚，方便我们跟进处理。"
          className={`${inputClass} resize-none`}
        />
        <span className="self-end text-xs text-foreground-400">{count}/500</span>
      </div>

      <input
        className="form-hp"
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {status === 'loading' ? '发送中…' : '发送留言'}
        </button>
      </div>

      {status === 'success' && (
        <p className="flex items-center gap-2 text-sm text-primary-700 sm:col-span-2">
          <i className="ri-checkbox-circle-line" />
          {message}
        </p>
      )}
      {status === 'error' && (
        <p className="flex items-center gap-2 text-sm text-primary-700 sm:col-span-2">
          <i className="ri-error-warning-line" />
          {message}
        </p>
      )}
    </form>
  );
}